<template>
  <div
    id="img-explorer"
    class="solvit-image-explorer d-flex flex-column"
    style="width: 100%; height: 100%; min-height: 0"
  >
    <!-- DefectListDrawer 모듈 리스트 제목과 동일한 rail 서브헤더 -->
    <v-card
      flat
      color="transparent"
      class="d-flex align-center flex-shrink-0 solvit-rail-subheader px-2"
    >
      <span class="text-caption font-weight-medium">
        {{ $t('common.imageListTitle') }}
      </span>
    </v-card>

    <v-simple-table class="flex-shrink-0 explorer-header-table">
      <template slot="default">
        <thead>
          <tr>
            <th class="text-left" width="30%">
              {{ $t('common.images') }}
            </th>
            <th class="text-left">
              {{ $t('common.fileName') }}
            </th>
            <th class="solvit-ie-trailing-col" aria-hidden="true" />
          </tr>
        </thead>
      </template>
    </v-simple-table>

    <RailListSkeleton
      v-if="showExplorerLoading"
      variant="image-row"
      :count="8"
      :row-height="virtualRowHeight"
    />
    <div
      v-else
      class="thermal-virtual-scroll-host flex-grow-1 d-flex flex-column"
      style="min-height: 0"
    >
      <v-virtual-scroll
        ref="thermalVirtualScroll"
        :items="thermalImages"
        :item-height="virtualRowHeight"
        :bench="18"
        class="flex-grow-1 thermal-virtual-scroll"
        style="min-height: 0"
      >
        <template #default="{ item: image, index }">
          <div
            :ref="'image-row-' + index"
            :class="{
              hovered: index === hoveredThermalImageIndex,
              selected: index === selectedThermalImageIndex
            }"
            :style="{ height: virtualRowHeight + 'px' }"
            tabindex="0"
            class="image-row d-flex align-center"
            @keydown.space.prevent="selectImage(image, index)"
            @keydown.enter.prevent="openDefectDialogForCreation(image, index)"
            @keydown.up.prevent="moveUp(image, index)"
            @keydown.down.prevent="moveDown(image, index)"
            @click.stop="onImageRowClick(image, index)"
            @mouseenter="setHoveredIndex(index)"
            @mouseleave="setHoveredIndex(-1)"
          >
            <div
              class="d-flex align-center flex-shrink-0 solvit-ie-thumb-cell"
              style="width: 30%; min-width: 0"
            >
              <v-badge
                :content="displayDefectCount(image)"
                :value="displayDefectCount(image)"
                :color="image.allDefectConfirmed ? 'error' : 'warning'"
                overlap
                bordered
              >
                <v-img
                  class="ma-0 image-thumbnail"
                  eager
                  :aspect-ratio="16 / 9"
                  width="90px"
                  height="100%"
                  :src="image.thumbImgUrl"
                  :alt="image.imgName"
                />
              </v-badge>
            </div>
            <div
              class="flex-grow-1 solvit-ie-filename-cell body-2"
              style="min-width: 0"
            >
              <RailListEllipsisTooltip
                key-prefix="ie-fn"
                :item-key="image.id != null ? image.id : index"
                :epoch="filenameTooltipEpoch"
                :text="image?.imgName || ''"
                :ellipsis="{ type: 'fitWidth', frontLength: 5, backLength: 11 }"
              />
            </div>
            <div class="flex-shrink-0 pr-1">
              <v-btn
                icon
                @click.stop.prevent="openDefectDialogForCreation(image, index)"
              >
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
            </div>
          </div>
        </template>
      </v-virtual-scroll>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import { isAppDebugEnabled } from '@/utils/debug/debugEnv';
  import { moduleDefectMatchesPk } from '@/utils/defect/defectShapeOverlay';
  import { getModuleDefectDetailCached } from '@/utils/moduleDefectDetailCache';

  export default {
    components: {
      RailListSkeleton: () => import('@/components/common/RailListSkeleton.vue'),
      RailListEllipsisTooltip: () => import('@/components/common/RailListEllipsisTooltip.vue')
    },
    props: {
      loading: {
        type: Boolean,
        default: false
      }
    },
    data: () => ({
      layer: '',
      eventTimingObserver: null,
      hoverRafId: null,
      pendingHoveredIndex: null,
      isDefectDialogTransitioning: false,
      isDebugMode: false,
      /**
       * v-virtual-scroll 행 높이(px). 썸네일(90×16/9) + 패딩 + v-badge overlap 이 잘리지 않도록 여유.
       * (고정 높이 안에서 overflow:hidden 제거만으로는 행 박스에 걸려 잘릴 수 있음)
       */
      virtualRowHeight: 65,
      /**
       * 가상 스크롤 시 행이 바뀌어도 v-tooltip 오버레이는 남을 수 있음(mouseleave 없음).
       * 스크롤마다 전부 remount 하지 않도록, 한 번에 한 번만 epoch 증가 후 짧은 idle 뒤 재무장.
       */
      filenameTooltipEpoch: 0,
      filenameTooltipScrollIdleTimer: null,
      filenameTooltipScrollArmed: true,
      detachVirtualScrollTooltipListener: null,
      /** fetchThermalImages 갱신 시 VVirtualScroll scrollTop 보존 */
      preservedThermalScrollTop: null
    }),

    computed: {
      domainName: {
        get() {
          return process.env.VUE_APP_DOMAIN ? process.env.VUE_APP_DOMAIN : '';
        }
      },
      ...mapGetters({
        thermalImages: 'dashboard/getThermalImages',
        getReportId: 'explorer/getReportId',
        getSite: 'explorer/getSite',
        getAiDefectLoading: 'dashboard/getAiDefectLoading',
        getThermalImagesLoading: 'dashboard/getThermalImagesLoading',
        defectInfoDialogOpen: 'defect/defectInfoDialogOpen',
        selectedThermalImageIndex: 'dashboard/getSelectedThermalImageIndex',
        selectedThermalImage: 'dashboard/getSelectedThermalImage',
        selectedThermalImageId: 'dashboard/getSelectedThermalImageId',
        hoveredThermalImageIndex: 'dashboard/getHoveredThermalImageIndex'
      }),

      showExplorerLoading() {
        return this.loading || this.getAiDefectLoading || this.getThermalImagesLoading;
      }
    },
    watch: {
      /** 로딩 해제 직후 flex 높이가 잡히기 전에 VVirtualScroll 이 0행만 그릴 수 있어 한 번 onScroll 갱신 */
      showExplorerLoading(val) {
        if (val) {
          return;
        }
        this.scheduleThermalScrollRestore();
      },
      getThermalImagesLoading(val, oldVal) {
        if (val && !oldVal) {
          const vs = this.$refs.thermalVirtualScroll;
          const top = vs?.$el?.scrollTop;
          if (top > 0) {
            this.preservedThermalScrollTop = top;
          }
        }
        if (oldVal === true && val === false) {
          this.$nextTick(() => {
            requestAnimationFrame(() => {
              this.restorePreservedThermalScroll();
              this.scheduleThermalScrollRestore();
            });
          });
        }
      },
      thermalImages() {
        if (this.preservedThermalScrollTop != null) {
          this.$nextTick(() => this.restorePreservedThermalScroll());
        }
      },
      //사각형 초기화 기준 -> reportId 변경 시 초기화
      getReportId(newReportId) {
        if (newReportId) {
          // EventBus 방식으로 변경했으므로 store 호출 제거
          this.debugLog('Report ID changed:', newReportId);
        }
      },
      getSite() {},
      selectedThermalImageIndex(newIndex) {
        this.debugLog('selectedThermalImageIndex changed:', newIndex);
        if (newIndex >= 0 && newIndex < this.thermalImages.length) {
          // 해당 행에 포커스 설정 (스크롤은 키보드 moveUp/moveDown 등에서만 — 여기서는 preventScroll 유지)
          this.$nextTick(() => {
            this.setFocus(newIndex, { scrollIntoView: false });
          });
        }
      },
      defectInfoDialogOpen(newValue, oldValue) {
        if (newValue) {
          this.isDefectDialogTransitioning = false;
        }
        /* 모달이 닫히면 포커스가 모달로 이동한 뒤 사라지므로, 같은 열화상 행으로 되돌려 ↑↓ 탐색 가능하게 함 */
        if (oldValue === true && newValue === false) {
          const idx = this.selectedThermalImageIndex;
          if (idx >= 0 && idx < this.thermalImages.length) {
            this.$nextTick(() => {
              requestAnimationFrame(() => {
                this.setFocus(idx, { scrollIntoView: true });
              });
            });
          }
        }
      }
    },
    methods: {
      debugLog(...args) {
        if (this.isDebugMode) {
          console.log(...args);
        }
      },
      debugWarn(...args) {
        if (this.isDebugMode) {
          console.warn(...args);
        }
      },
      displayDefectCount(image) {
        if (!image) return 0;
        const list = image.moduleDefects;
        if (Array.isArray(list) && list.length > 0) {
          return list.length;
        }
        return image.defectCount || 0;
      },
      ...mapActions({
        toggleActive: 'explorer/toggleActive',
        setShowExplorer: 'explorer/setShowExplorer',
        selectTreeItem: 'explorer/selectTreeItem'
      }),
      /**
       * 행 클릭: 선택 + 키보드 포커스(:focus-visible 보라 테두리)를 항상 해당 행에 둠.
       * 동일 행 재클릭 시에도 selectImage는 조기 return 하므로 여기서 포커스를 보장한다.
       */
      onImageRowClick(image, imageIndex) {
        this.selectImage(image, imageIndex);
        if (image?.id == undefined) {
          return;
        }
        this.$nextTick(() => {
          this.setFocus(imageIndex, { scrollIntoView: false });
        });
      },
      //이미지 선택
      selectImage(image, imageIndex) {
        if (image?.id == undefined) {
          return;
        }
        if (
          this.selectedThermalImageId === image.id &&
          this.selectedThermalImageIndex === imageIndex
        ) {
          return;
        }
        this.$store.commit('dashboard/SET_SELECTED_THERMAL_IMAGE', {
          image: image,
          index: imageIndex
        });
      },
      moveUp(image, index) {
        if (index > 0) {
          this.$store.commit('dashboard/SET_HOVERED_THERMAL_IMAGE_INDEX', index - 1);
          this.$nextTick(() => {
            this.setFocus(index - 1, { scrollIntoView: true });
            this.selectImage(this.thermalImages[index - 1], index - 1);
          });
        }
      },
      moveDown(image, index) {
        if (index < this.thermalImages.length - 1) {
          const next = index + 1;
          this.$store.commit('dashboard/SET_HOVERED_THERMAL_IMAGE_INDEX', next);
          this.$nextTick(() => {
            this.setFocus(next, { scrollIntoView: true });
            this.selectImage(this.thermalImages[next], next);
          });
        }
      },
      /**
       * 가상 리스트 뷰포트 안에 index 행이 들어오도록 스크롤만 조정 (DOM 전부 그리지 않음)
       */
      restorePreservedThermalScroll() {
        const vs = this.$refs.thermalVirtualScroll;
        const top = this.preservedThermalScrollTop;
        if (!vs?.$el || top == null || top <= 0) {
          return;
        }
        vs.$el.scrollTop = top;
        if (typeof vs.onScroll === 'function') {
          vs.onScroll();
        }
      },
      scheduleThermalScrollRestore() {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.restorePreservedThermalScroll();
            const idx = this.selectedThermalImageIndex;
            if (idx >= 0 && idx < this.thermalImages.length) {
              this.ensureVirtualIndexVisible(idx);
            }
            const vs = this.$refs.thermalVirtualScroll;
            if (vs && typeof vs.onScroll === 'function') {
              vs.onScroll();
            }
            this.attachThermalScrollTooltipDismiss();
          });
        });
      },
      ensureVirtualIndexVisible(index, attempt = 0) {
        const vs = this.$refs.thermalVirtualScroll;
        if (
          !vs ||
          !vs.$el ||
          typeof index !== 'number' ||
          index < 0 ||
          !this.thermalImages ||
          index >= this.thermalImages.length
        ) {
          return;
        }
        const el = vs.$el;
        const h = this.virtualRowHeight;
        const itemTop = index * h;
        const itemBottom = itemTop + h;
        const viewTop = el.scrollTop;
        const clientH = el.clientHeight;
        if (!clientH) {
          if (attempt < 10) {
            requestAnimationFrame(() => this.ensureVirtualIndexVisible(index, attempt + 1));
          }
          return;
        }
        const viewBottom = viewTop + clientH;
        let nextTop = viewTop;
        if (itemTop < viewTop) {
          nextTop = itemTop;
        } else if (itemBottom > viewBottom) {
          nextTop = Math.max(0, itemBottom - clientH);
        }
        const maxScroll = Math.max(0, this.thermalImages.length * h - clientH);
        nextTop = Math.min(Math.max(0, nextTop), maxScroll);
        if (nextTop !== viewTop) {
          el.scrollTop = nextTop;
        }
        const vsComp = this.$refs.thermalVirtualScroll;
        if (vsComp && typeof vsComp.onScroll === 'function') {
          vsComp.onScroll();
        }
      },
      setFocus(index, { scrollIntoView = false } = {}) {
        if (scrollIntoView) {
          this.ensureVirtualIndexVisible(index);
        }
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            if (scrollIntoView) {
              this.ensureVirtualIndexVisible(index);
            }
            const ref = this.$refs[`image-row-${index}`];
            const rowEl = Array.isArray(ref) ? ref[0] : ref;
            if (rowEl && typeof rowEl.focus === 'function') {
              rowEl.focus({ preventScroll: true });
            }
          });
        });
      },
      /**
       * @props image: 이미지 정보
       * @props index: 이미지 인덱스
       * @props defect: 결함 정보
       * @props isDefectSelected: 결함 선택 여부
       * @props forceReset: 다이얼로그 초기화 여부
       */
      openDefectDialogForEditing(thermalImage, imageIndex, defect) {
        this.selectImage(thermalImage, imageIndex);

        if (defect) {
          this.$store.dispatch('defect/openDefectFromMap', {
            thermalImage: thermalImage,
            defect: defect
          });
        }
      },
      openDefectDialogForCreation(thermalImage, imageIndex) {
        if (this.isDefectDialogTransitioning) {
          return;
        }
        if (thermalImage?.id != null && typeof imageIndex === 'number') {
          this.selectImage(thermalImage, imageIndex);
        }
        this.isDefectDialogTransitioning = true;
        this.$store.dispatch('defect/openImageForNewDefect', {
          thermalImage: thermalImage
        });
        requestAnimationFrame(() => {
          if (!this.defectInfoDialogOpen) {
            this.isDefectDialogTransitioning = false;
          }
        });
      },
      /**
       * 지도/WFS에서 넘어온 결함 PK와 열화상에 embedded된 PanelDefect 매칭.
       * DefectOverlayPopupContent.resolveDefectPkFromProps·findDefectInContentList 과 동일하게
       * id만이 아니라 seq·moduleDefectSeq 도 본다 (목록 쪽은 seq만 있는 경우가 있음).
       */
      matchesModuleDefectPk(defect, defectId) {
        const pid = Number(defectId);
        if (!Number.isFinite(pid)) {
          return false;
        }
        return (
          Number(defect?.id) === pid ||
          Number(defect?.seq) === pid ||
          Number(defect?.moduleDefectSeq) === pid
        );
      },
      /** 지도에서 해당 결함 선택시 */
      handleOpenDefectWindowEvent(evt) {
        this.debugLog('[IMAGE EXPLORER] evt.detail:', evt.detail);

        const defectId = Number(evt.detail?.defectId);
        if (!Number.isFinite(defectId) || defectId <= 0) {
          console.warn('[IMAGE EXPLORER] 유효하지 않은 defectId:', evt.detail?.defectId);
          return;
        }

        let thermalImage = null;
        let panelDefect = null;

        this.debugLog('[IMAGE EXPLORER] 찾을 defectId:', defectId);

        for (const image of this.thermalImages) {
          if (image.moduleDefects) {
            panelDefect = image.moduleDefects.find(defect =>
              this.matchesModuleDefectPk(defect, defectId)
            );
            if (panelDefect) {
              thermalImage = image;
              this.debugLog('[IMAGE EXPLORER] 결함을 찾음:', { thermalImage, panelDefect });
              break;
            }
          }
        }

        if (!thermalImage) {
          thermalImage = this.resolveThermalImageForDefectOpen(evt.detail?.thermalImgSeq);
        }

        if (thermalImage && panelDefect) {
          this.openDefectDetailFromMap(thermalImage, panelDefect);
          return;
        }

        if (thermalImage) {
          this.openDefectDetailById(thermalImage, defectId);
          return;
        }

        this.openDefectDetailWithApiFallback(defectId, evt.detail?.thermalImgSeq);
      },
      resolveThermalImageForDefectOpen(thermalImgSeq) {
        if (thermalImgSeq != null && thermalImgSeq !== '') {
          const fromSeq = this.thermalImages.find(img => Number(img?.id) === Number(thermalImgSeq));
          if (fromSeq) {
            return fromSeq;
          }
        }
        const selected = this.$store.getters['dashboard/getSelectedThermalImage'];
        if (selected?.id != null) {
          return selected;
        }
        return null;
      },
      openDefectDetailFromMap(thermalImage, panelDefect) {
        const imageIndex = this.thermalImages.findIndex(
          imageItem => imageItem.id === thermalImage.id
        );
        this.debugLog('[IMAGE EXPLORER] openDefectDetailDialog 호출:', {
          thermalImage,
          imageIndex,
          panelDefect
        });
        this.selectImage(thermalImage, imageIndex);
        this.$store.dispatch('defect/openDefectFromMap', {
          thermalImage,
          defect: panelDefect
        });
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            if (imageIndex >= 0) {
              this.ensureVirtualIndexVisible(imageIndex);
            }
          });
        });
      },
      openDefectDetailById(thermalImage, defectId) {
        const imageIndex = this.thermalImages.findIndex(
          imageItem => imageItem.id === thermalImage.id
        );
        this.debugLog('[IMAGE EXPLORER] openDefectFromList fallback:', {
          thermalImage,
          imageIndex,
          defectId
        });
        this.selectImage(thermalImage, imageIndex);
        this.$store.dispatch('defect/openDefectFromList', {
          thermalImage,
          defectId
        });
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            if (imageIndex >= 0) {
              this.ensureVirtualIndexVisible(imageIndex);
            }
          });
        });
      },
      async openDefectDetailWithApiFallback(defectId, thermalImgSeq) {
        try {
          const detail = await getModuleDefectDetailCached(defectId);
          if (!detail || typeof detail !== 'object') {
            console.warn('[IMAGE EXPLORER] 결함 상세 API 조회 실패:', defectId);
            return;
          }
          const thermalIdFromDetail =
            detail.solarThermalImage?.id ?? detail._embedded?.solarThermalImage?.id ?? null;
          const thermalId =
            thermalIdFromDetail != null && thermalIdFromDetail !== ''
              ? Number(thermalIdFromDetail)
              : thermalImgSeq != null && thermalImgSeq !== ''
                ? Number(thermalImgSeq)
                : null;
          let thermalImage = null;
          if (Number.isFinite(thermalId) && thermalId > 0) {
            thermalImage = this.thermalImages.find(img => Number(img?.id) === thermalId);
          }
          if (!thermalImage && detail.solarThermalImage) {
            thermalImage = {
              id: thermalIdFromDetail,
              imgName:
                detail.solarThermalImage?.imgName ||
                detail._embedded?.solarThermalImage?.imgName ||
                ''
            };
          }
          if (!thermalImage) {
            console.warn('[IMAGE EXPLORER] 열화상을 찾을 수 없음:', {
              defectId,
              thermalId
            });
            return;
          }
          const panelDefect = moduleDefectMatchesPk(detail, defectId) ? detail : { id: defectId };
          this.openDefectDetailFromMap(thermalImage, panelDefect);
        } catch (error) {
          console.error('[IMAGE EXPLORER] openDefectDetailWithApiFallback 실패:', error);
        }
      },
      setHoveredIndex(index) {
        if (this.defectInfoDialogOpen || this.isDefectDialogTransitioning) {
          return;
        }
        this.pendingHoveredIndex = index;
        if (this.hoverRafId) {
          return;
        }
        this.hoverRafId = requestAnimationFrame(() => {
          const nextIndex = this.pendingHoveredIndex;
          this.pendingHoveredIndex = null;
          this.hoverRafId = null;
          if (this.hoveredThermalImageIndex === nextIndex) {
            return;
          }
          this.$store.commit('dashboard/SET_HOVERED_THERMAL_IMAGE_INDEX', nextIndex);
        });
      },
      handleFilenamePointerDown() {},
      dismissFilenameTooltipsOnVirtualScroll() {
        if (this.filenameTooltipScrollArmed) {
          this.filenameTooltipEpoch += 1;
          this.filenameTooltipScrollArmed = false;
        }
        clearTimeout(this.filenameTooltipScrollIdleTimer);
        this.filenameTooltipScrollIdleTimer = setTimeout(() => {
          this.filenameTooltipScrollArmed = true;
          this.filenameTooltipScrollIdleTimer = null;
        }, 200);
      },
      attachThermalScrollTooltipDismiss() {
        const vs = this.$refs.thermalVirtualScroll;
        const el = vs && vs.$el;
        if (typeof this.detachVirtualScrollTooltipListener === 'function') {
          this.detachVirtualScrollTooltipListener();
          this.detachVirtualScrollTooltipListener = null;
        }
        if (!el) {
          return;
        }
        const handler = () => this.dismissFilenameTooltipsOnVirtualScroll();
        el.addEventListener('scroll', handler, { passive: true });
        this.detachVirtualScrollTooltipListener = () => {
          el.removeEventListener('scroll', handler);
        };
      }
    },
    mounted() {
      this.isDebugMode = isAppDebugEnabled();
      // window 이벤트를 직접 리스닝
      window.addEventListener('open-defect-window', this.handleOpenDefectWindowEvent);
      this.$nextTick(() => {
        this.attachThermalScrollTooltipDismiss();
      });
    },
    beforeUnmount() {
      // window 이벤트 리스너 제거
      window.removeEventListener('open-defect-window', this.handleOpenDefectWindowEvent);
      if (this.hoverRafId) {
        cancelAnimationFrame(this.hoverRafId);
        this.hoverRafId = null;
      }
      this.pendingHoveredIndex = null;
      if (this.eventTimingObserver && typeof this.eventTimingObserver.disconnect === 'function') {
        this.eventTimingObserver.disconnect();
        this.eventTimingObserver = null;
      }
      clearTimeout(this.filenameTooltipScrollIdleTimer);
      this.filenameTooltipScrollIdleTimer = null;
      if (typeof this.detachVirtualScrollTooltipListener === 'function') {
        this.detachVirtualScrollTooltipListener();
        this.detachVirtualScrollTooltipListener = null;
      }
    }
  };
</script>

<style>
  .container {
    overflow: hidden;
  }

  /* .image-table{
  height: 700px;
} */
  .image-row #mdi-pencil {
    color: var(--v-text-base);
  }

  #img-explorer .explorer-header-table {
    width: 100%;
  }

  #img-explorer .explorer-header-table thead th {
    padding-top: 6px !important;
    padding-bottom: 6px !important;
    font-size: 0.75rem !important;
    font-weight: 600 !important;
  }

  /* 예전 v-simple-table td 와 맞춤: 썸네일 셀 padding: 6px 0 0 8px, 파일명 셀은 td 수평 패딩에 가깝게 */
  #img-explorer .solvit-ie-thumb-cell {
    padding: 6px 0 0 8px;
    box-sizing: border-box;
    /* v-badge overlap 은 이미지 밖으로 그려짐 — hidden 이면 뱃지가 잘림 */
    overflow: visible;
  }

  #img-explorer .solvit-ie-filename-cell {
    padding: 0 16px;
    box-sizing: border-box;
    min-width: 0;
    overflow: hidden;
  }

  #img-explorer .solvit-ie-trailing-col {
    width: 48px;
    min-width: 48px;
    max-width: 48px;
    padding: 4px 8px 4px 4px !important;
    box-sizing: border-box;
  }

  #img-explorer .thermal-virtual-scroll .image-row {
    box-sizing: border-box;
    border-bottom: 1px solid var(--v-borderSubtle-base);
    overflow: visible;
  }

  /* 가상 스크롤 행 박스가 뱃지를 자르지 않도록 (Vuetify __item 기본은 visible) */
  #img-explorer .thermal-virtual-scroll .v-virtual-scroll__item {
    overflow: visible;
  }

  /* 선택 행: main.css --solvit-row-selected-on-surface 와 동기 */
  .v-application.theme--light #img-explorer .thermal-virtual-scroll .image-row.selected {
    background-color: var(--solvit-row-selected-on-surface);
  }
  .v-application.theme--dark #img-explorer .thermal-virtual-scroll .image-row.selected {
    background-color: var(--solvit-row-selected-on-surface);
  }

  .v-application.theme--light #img-explorer .thermal-virtual-scroll .image-row.selected,
  .v-application.theme--light
    #img-explorer
    .thermal-virtual-scroll
    .image-row.selected
    .text-overflow-ellipsis {
    color: var(--v-text-base);
  }
  .v-application.theme--dark #img-explorer .thermal-virtual-scroll .image-row.selected,
  .v-application.theme--dark
    #img-explorer
    .thermal-virtual-scroll
    .image-row.selected
    .text-overflow-ellipsis {
    color: var(--v-text-base);
  }

  /*
 * 마우스 클릭 시 :focus-visible 이 안 잡히는 브라우저가 많아, 선택 행은 항상 primary 테두리.
 * :focus { outline: none } 은 .selected:focus 보다 우선하지 않도록 아래에서 덮음.
 */
  #img-explorer .thermal-virtual-scroll .image-row:focus {
    outline: none;
  }
  #img-explorer .thermal-virtual-scroll .image-row.selected {
    outline: 2px solid var(--v-primary-base);
    outline-offset: -2px;
  }
  #img-explorer .thermal-virtual-scroll .image-row.selected:focus {
    outline: 2px solid var(--v-primary-base);
    outline-offset: -2px;
  }
  #img-explorer .thermal-virtual-scroll .image-row:focus-visible {
    outline: 2px solid var(--v-primary-base);
    outline-offset: -2px;
  }

  #img-explorer .thermal-virtual-scroll .image-row.hovered:not(.selected) {
    background-color: var(--v-surface-base);
  }

  .image-thumbnail {
    border-top: 1px solid var(--v-borderSubtle-base);
    border-bottom: 1px solid var(--v-borderSubtle-base);
  }
</style>
